import { useMemo, useState } from 'react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Spinner } from '@/components/ui/Spinner'
import { useProspectsList, useProspectTags } from '@/hooks/useProspection'
import { useCreateCampaign } from '@/hooks/useEmailCampaigns'
import { STATUT_LABELS } from '@/types/prospection'
import type { ProspectStatut, Prospect } from '@/types/prospection'
import type { CampaignFilters } from '@/types/emailCampaigns'
import toast from 'react-hot-toast'
import { X } from 'lucide-react'

interface Props {
  onClose: () => void
  onCreated: () => void
}

export default function CampaignWizard({ onClose, onCreated }: Props) {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1)
  const [name, setName] = useState('')
  const [statut, setStatut] = useState<ProspectStatut | ''>('')
  const [hasSite, setHasSite] = useState<'' | 'oui' | 'non'>('')
  const [tag, setTag] = useState('')
  const [throttle, setThrottle] = useState(30)
  const [winStart, setWinStart] = useState('09:00')
  const [winEnd, setWinEnd] = useState('18:00')
  const [excluded, setExcluded] = useState<Set<string>>(new Set())

  const hasWebsite = hasSite === 'oui' ? true : hasSite === 'non' ? false : null
  const filters: CampaignFilters = { statut, hasWebsite, tag }

  const { data: tagsData } = useProspectTags()
  const { data, isLoading } = useProspectsList({
    search: '',
    statut,
    hasWebsite,
    tag,
    page: 1,
    limit: 1000,
    sortBy: 'score_priorite',
    sortOrder: 'desc',
  })

  const eligible = useMemo<Prospect[]>(
    () =>
      (data?.prospects ?? []).filter(
        (p) =>
          p.email &&
          p.email_subject &&
          p.email_body &&
          p.email_status !== 'unsubscribed',
      ),
    [data],
  )
  const selected = useMemo(
    () => eligible.filter((p) => !excluded.has(p.id)),
    [eligible, excluded],
  )

  const create = useCreateCampaign()

  async function launch() {
    if (!name.trim()) {
      toast.error('Donne un nom à la campagne')
      return
    }
    if (!selected.length) {
      toast.error('Aucun prospect éligible')
      return
    }
    try {
      await create.mutateAsync({
        name: name.trim(),
        filters,
        throttle_per_day: throttle,
        send_window_start: winStart,
        send_window_end: winEnd,
        timezone: 'Europe/Paris',
        start_at: null,
        prospects: selected.map((p) => ({
          prospect_id: p.id,
          email_to: p.email!,
          email_subject: p.email_subject!,
          email_body: p.email_body!,
        })),
      })
      toast.success(`Campagne lancée (${selected.length} emails)`)
      onCreated()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erreur')
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <Card padding="none" className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center px-6 py-4 border-b border-brown-100">
          <h3 className="text-base font-medium text-brown-900">
            Nouvelle campagne — étape {step}/4
          </h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {step === 1 && (
          <div className="p-6 space-y-4">
            <Input
              label="Nom de la campagne"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: PME sans site - mai 2026"
            />
            <Select
              label="Statut"
              value={statut}
              onChange={(e) => setStatut(e.target.value as ProspectStatut | '')}
            >
              <option value="">Tous les statuts</option>
              {Object.entries(STATUT_LABELS).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </Select>
            <Select
              label="Site web"
              value={hasSite}
              onChange={(e) => setHasSite(e.target.value as '' | 'oui' | 'non')}
            >
              <option value="">Indifférent</option>
              <option value="oui">Avec site</option>
              <option value="non">Sans site</option>
            </Select>
            <Select
              label="Tag"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
            >
              <option value="">Tous</option>
              {(tagsData ?? []).map((t: string) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
            <div className="flex justify-end">
              <Button onClick={() => setStep(2)} disabled={!name.trim()}>
                Suivant
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="p-6 space-y-3">
            {isLoading ? (
              <Spinner />
            ) : (
              <>
                <p className="text-sm text-brown-600">
                  {eligible.length} prospect(s) éligible(s) — {selected.length} sélectionné(s)
                </p>
                <div className="max-h-96 overflow-y-auto border border-brown-100 rounded">
                  {eligible.map((p) => (
                    <label
                      key={p.id}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-cream-50 border-b border-brown-100"
                    >
                      <input
                        type="checkbox"
                        checked={!excluded.has(p.id)}
                        onChange={() => {
                          setExcluded((s) => {
                            const n = new Set(s)
                            if (n.has(p.id)) n.delete(p.id)
                            else n.add(p.id)
                            return n
                          })
                        }}
                      />
                      <span className="text-sm text-brown-900 flex-1">
                        {p.nom_commercial || p.raison_sociale}
                      </span>
                      <span className="text-xs text-brown-500">{p.email}</span>
                    </label>
                  ))}
                  {!eligible.length && (
                    <p className="p-4 text-sm text-brown-500">
                      Aucun prospect éligible. Vérifie que les prospects ont email,
                      email_subject et email_body, et qu'ils ne sont pas désinscrits.
                    </p>
                  )}
                </div>
              </>
            )}
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(1)}>
                Retour
              </Button>
              <Button onClick={() => setStep(3)} disabled={!selected.length}>
                Suivant
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="p-6 space-y-4">
            <Input
              label="Emails par jour"
              type="number"
              value={throttle}
              onChange={(e) => setThrottle(Number(e.target.value))}
              min={1}
              max={500}
            />
            <Input
              label="Heure début"
              type="time"
              value={winStart}
              onChange={(e) => setWinStart(e.target.value)}
            />
            <Input
              label="Heure fin"
              type="time"
              value={winEnd}
              onChange={(e) => setWinEnd(e.target.value)}
            />
            <p className="text-xs text-brown-500">Fuseau : Europe/Paris</p>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(2)}>
                Retour
              </Button>
              <Button
                onClick={() => setStep(4)}
                disabled={winEnd <= winStart || throttle < 1}
              >
                Suivant
              </Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="p-6 space-y-4">
            <p className="text-sm text-brown-600">Aperçu de 3 emails au hasard :</p>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {selected.slice(0, 3).map((p) => (
                <div key={p.id} className="border border-brown-100 rounded p-3">
                  <p className="text-xs text-brown-500">À : {p.email}</p>
                  <p className="text-sm font-medium text-brown-900 mt-1">
                    {p.email_subject}
                  </p>
                  <pre className="text-xs text-brown-700 whitespace-pre-wrap mt-2 font-sans">
                    {p.email_body}
                  </pre>
                </div>
              ))}
            </div>
            <p className="text-xs text-brown-500">
              Le footer RGPD sera ajouté automatiquement par le worker à l'envoi.
            </p>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(3)}>
                Retour
              </Button>
              <Button onClick={launch} disabled={create.isPending}>
                {create.isPending ? 'Lancement…' : `Lancer (${selected.length} emails)`}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
